import { Router } from '@lightningjs/sdk'

import { transparent } from '../../constants/colors'
import { carouselsPrefix, LEFT_SAFE_ZONE_MARGIN } from '../../constants/general'
import { AppConfig } from '../../services/appConfig'
import { LinkModelStylesEnum } from '../../types/flo-enums'
import {
  MessageModel,
  TvCarouselContainerModel,
  TvRowsContainerModelItem,
} from '../../types/flo-experience'
import {
  CAROUSEL_MARGIN_BOTTOM,
  PAGINATION_OFFSET,
  toCarouselRef,
  toContainer,
} from '../../utils/carouselUtils'
import { areObjsEqual } from '../../utils/misc'
import { PaginateBlock } from '../PaginateBlock/PaginateBlock'
import { PaginationErrorMessage } from '../PartialErrorMessage/PaginationErrorMessage'
import SpeechComp from '../speechComp/speechComp'
import TvBrowseLayout from '../TvBrowseLayout/TvBrowseLayout'

export const FALLBACK_MESSAGE_ITEM: MessageModel = {
  id: 'non-unique-id',
  type: 'message',
  title: null,
  text: 'There are currently no live streams or related content.',
  style: 'no-content',
  action: null,
}

export default class TvRowsContainer extends SpeechComp {
  private _data: Array<TvRowsContainerModelItem>
  private _currentOffset = 0
  private _currentCarouselIndex = 0
  private _totalCarousels = 0
  private _withSpotlight = false
  private _hasPagination = false
  private _paginationError = false
  private _isPagePending = false
  private _sectionText = 'Not Loaded Yet Rows'

  static _template() {
    return {
      w: (w) => w,
      h: (h) => h,
      rect: true,
      color: transparent,
      collision: true,
      CarouselsContainer: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: transparent,
        flex: {
          direction: 'column',
        },
      },
    }
  }

  get carouselsContainer() {
    return this.tag('CarouselsContainer')
  }

  get sectionText(): string {
    return this._sectionText
  }

  set sectionText(v: string) {
    this._sectionText = v
  }

  set withSpotlight(v) {
    this._withSpotlight = v
  }

  set data(v: Array<TvRowsContainerModelItem>) {
    this._data = v

    if (this._data.length > 0) {
      this.renderContainers(this._data)
    } else {
      // On very rare circumstances, some TvRowsContainerModel will have an empty array of items. This patches this event
      this._data = [FALLBACK_MESSAGE_ITEM]
      this.renderContainers(this._data)
      this._refocus()
    }
  }

  get currentContainer() {
    return this.getChild(this._currentCarouselIndex)
  }

  get isFirstContainerFocused() {
    return this._currentCarouselIndex === 0
  }

  get isLastContainerFocused() {
    return this._currentCarouselIndex === this._totalCarousels - 1
  }

  get currentOffset() {
    return this._currentOffset
  }

  get rowsContainerHeight() {
    return this.finalH
  }

  get rowsContainerCenter() {
    return this.rowsContainerHeight / 2
  }

  set offset(v) {
    this.y = v
  }

  get currentCarouselIndex() {
    return this._currentCarouselIndex
  }

  set currentCarouselIndex(index: number) {
    this._currentCarouselIndex = index
  }

  set currentOffset(v: number) {
    this.carouselsContainer.y = v
  }

  get data() {
    return this._data
  }

  _getFocused() {
    return this.currentContainer
  }

  _handleUp() {
    if (this.isFirstContainerFocused) {
      return false
    } else {
      this.handleScroll({ deltaY: -1 })
    }
  }

  _handleDown() {
    if (this.isLastContainerFocused) {
      return false
    } else {
      this.handleScroll({ deltaY: 1 })
    }
  }

  _handleLeft() {
    if (Router.getActivePage().widgets.menu.visible) {
      Router.focusWidget('Menu')
    } else {
      return false
    }
  }

  renderContainers(containers: Array<TvRowsContainerModelItem>) {
    this._hasPagination =
      containers.findIndex((container) => container.style === LinkModelStylesEnum.Paginate) > -1
    const children = containers.map((container, index) => toContainer(container, index))

    const finalHeight = children?.reduce((sum, child) => sum + child?.h, 0) ?? 0

    // Needed since at times an instance of NavHub will get reused and not clearing the children creates a weird glitch with overlapping carousels
    this.carouselsContainer?.childList?.clear()
    this._currentCarouselIndex = 0

    this._totalCarousels = children.length - (this._hasPagination ? 1 : 0)

    this.sectionText = this._totalCarousels + ' Row' + (this._totalCarousels > 1 ? 's' : '')

    this.carouselsContainer.patch({
      children: children,
      h: finalHeight,
      y: 0,
    })
  }

  handleScroll({ deltaY }) {
    // Make sure we have the latest coordinates before doing scrolling calculations
    this.stage.update()

    const hasSpotlight = (this.parent as TvBrowseLayout).hasSpotlight ?? false
    const isGoingDown = deltaY > 0

    if (
      (!isGoingDown && this.isFirstContainerFocused) ||
      (isGoingDown && this.isLastContainerFocused) ||
      (isGoingDown && this._isPagePending && this.isLastContainerFocused)
    ) {
      return
    }

    this._currentCarouselIndex += isGoingDown ? 1 : -1

    if (this._withSpotlight) {
      this.fireAncestors('$toggleSpotlightVisibility', this.isFirstContainerFocused)
    }

    const currentContainerOffset = this.currentContainer.finalY
    const currentContainerHeight = this.currentContainer.finalH

    if (
      this.isFirstContainerFocused ||
      (this._currentCarouselIndex >= 0 && this._currentCarouselIndex <= 1 && !hasSpotlight)
    ) {
      this._currentOffset = 0
    } else if (this.isLastContainerFocused) {
      const reqScrollPosition = currentContainerOffset - this.finalH + currentContainerHeight
      this._currentOffset =
        -reqScrollPosition -
        this.finalY -
        CAROUSEL_MARGIN_BOTTOM -
        (this._hasPagination ? PAGINATION_OFFSET : 0)

      if (this._hasPagination && !this._paginationError) {
        this.loadNextPage()
      }
    } else {
      const reqScrollPosition =
        currentContainerOffset - this.rowsContainerCenter + currentContainerHeight / 2
      this._currentOffset = -reqScrollPosition - this.finalY
    }

    this.scroll(this.currentOffset)
  }

  scroll(v: number) {
    this.carouselsContainer.setSmooth('y', v, {
      duration: 0.2,
    })
  }

  getChild(index: number) {
    return this.carouselsContainer.childList.getByRef(toCarouselRef(index))
  }

  async loadNextPage(pageBlock?: PaginateBlock) {
    if (this._isPagePending) return

    const paginationLink = pageBlock ?? (this.carouselsContainer.childList.last as PaginateBlock)

    if (paginationLink?.style === LinkModelStylesEnum.Paginate && paginationLink?.url) {
      this._isPagePending = true
      paginationLink.toggleLoader(true)
      const page = await AppConfig.shared.experienceApi.getHomePartial(paginationLink.url)
      const items = page?.data?.items

      if (items?.length > 0) {
        this._hasPagination = false

        const lastIndex = this._data.length - 1

        this._data.splice(-1, 1, ...items)

        const children = items.map((item, index) => toContainer(item, index, lastIndex))

        this.carouselsContainer.childList.remove(this.carouselsContainer.childList.last)

        this.carouselsContainer.childList.a(children)

        this._hasPagination =
          page.data.items.findIndex((item) => item.style === LinkModelStylesEnum.Paginate) > -1

        this._totalCarousels += children.length - (this._hasPagination ? 1 : 0)

        this.fireAncestors('$nextPageLoadComplete')
      } else if (items?.length === 0) {
        this._hasPagination = false
        paginationLink.toggleLoader(false)
        this.carouselsContainer.childList.remove(this.childList.last)

        this.scroll(this._currentOffset + PAGINATION_OFFSET)
      } else if (page?.error) {
        const error = {
          x: -LEFT_SAFE_ZONE_MARGIN,
          type: PaginationErrorMessage,
          ref: toCarouselRef(this._totalCarousels),
          retryAction: () => {
            this._currentCarouselIndex = this._totalCarousels - 2
            this._paginationError = false
            this._totalCarousels--
            this.loadNextPage(paginationLink)
          },
        }

        this.carouselsContainer.childList.replace(error, this.carouselsContainer.childList.last)
        this._totalCarousels++
        this._paginationError = true
      }

      this._isPagePending = false
    }
  }

  refreshCarousels(refreshCarousels: TvCarouselContainerModel[]) {
    refreshCarousels.forEach((match) => {
      const matchPrefix = Object.values(carouselsPrefix).find((prefix) => match.id.includes(prefix))

      if (!matchPrefix) return

      const index = this._data.findIndex((item) => item.id.includes(matchPrefix))

      if (index !== -1 && !areObjsEqual(match, this._data[index])) {
        this._data[index] = match

        const container = toContainer(match, index)

        this.carouselsContainer.childList.replace(
          container,
          this.carouselsContainer.children[index],
        )
      }
    })
  }
}
